import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu, X, Heart, ChevronDown,
  Home, Info, Users, ShoppingBag,
  BarChart3, Briefcase,
  Sparkles, GraduationCap,
  Shield, FileText, Globe, Award,
  BookOpen, Target, Users2,
  HeartHandshake, Image, MessageSquare, History,
  LogOut, LayoutDashboard
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import logo from "../../images/Headerlogo.png";
import "./NavbarLayout.css";

/* ─── Navigation structure ─── */
const impactDropdown = [
  { label: "Impact Numbers", to: "/impact?tab=numbers", icon: BarChart3 },
  { label: "Transparency", to: "/impact?tab=transparency", icon: Shield },
  { label: "Annual Report", to: "/impact?tab=report", icon: FileText },
  { label: "SDG Goals", to: "/impact?tab=sdg", icon: Globe },
  { label: "Research Papers", to: "/impact?tab=research", icon: BookOpen },
];

const aboutDropdown = [
  { label: "Our Story", to: "/ourstory", icon: History },
  { label: "Our Mission & Vision", to: "/missionvision", icon: Target },
  { label: "Our Team", to: "/ourteam", icon: Users2 }
];

const programsDropdown = [
  { label: "CEP", to: "/cep", icon: GraduationCap },
  { label: "Volunteering Projects", to: "/engagements", icon: Sparkles },
  { label: "Gallery", to: "/gallery", icon: Image },
  { label: "Testimonials", to: "/testimonials", icon: MessageSquare },
];

const aboutPaths = ["/wwd", "/ourstory", "/missionvision", "/ourteam", "/wkm", "/contactus", "/collaborations", "/privacypolicy"];
const programPaths = ["/gallery", "/engagements", "/testimonials", "/cep", "/inspirations", "/newsroom", "/project-kul"];

const NavbarLayout = ({ children }) => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);
  const [impactOpen, setImpactOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(true);

  const aboutRef = useRef(null);
  const programsRef = useRef(null);
  const impactRef = useRef(null);
  const mobileDrawerRef = useRef(null);

  // Check specifically if the user is viewing a Dashboard route
  const isDashboardRoute =
    location.pathname.startsWith("/student/dashboard") ||
    location.pathname.startsWith("/admin/dashboard") ||
    location.pathname.startsWith("/volunteer/dashboard") ||
    location.pathname.startsWith("/dashboard") ||
    location.pathname.includes("dashboard");

  const handleLogout = () => {
    if (logout) {
      logout();
    } else {
      localStorage.clear();
      sessionStorage.clear();
    }
    navigate("/volunteer/login", { replace: true });
  };

  // Active detection
  const isActive = useCallback((path) => location.pathname === path, [location.pathname]);
  const isExactActive = useCallback((path) => location.pathname + location.search === path, [location.pathname, location.search]);
  const isAboutActive = aboutPaths.some((p) => location.pathname.startsWith(p));
  const isProgramsActive = programPaths.some((p) => location.pathname.startsWith(p));
  const isImpactActive = location.pathname.startsWith("/impact");

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
    setAboutOpen(false);
    setProgramsOpen(false);
    setImpactOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname, location.search]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (aboutRef.current && !aboutRef.current.contains(e.target)) setAboutOpen(false);
      if (programsRef.current && !programsRef.current.contains(e.target)) setProgramsOpen(false);
      if (impactRef.current && !impactRef.current.contains(e.target)) setImpactOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Close mobile drawer on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (mobileOpen && mobileDrawerRef.current && !mobileDrawerRef.current.contains(e.target)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [mobileOpen]);

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const bannerHeight = bannerVisible ? 40 : 0;

  return (
    <div className="min-h-screen bg-ku-cream">

      {/* ─── 21 Years Celebration Banner ─── */}
      {bannerVisible && (
        <div className="ku-celebration-banner">
          <div className="ku-celebration-banner-shimmer" />
          <div className="flex items-center justify-center gap-2 relative z-10">
            <Award className="w-3.5 h-3.5 text-ku-gold" />
            <span className="text-xs sm:text-sm font-accent font-semibold tracking-wide">
              Celebrating <span className="text-ku-gold font-extrabold">21 Years</span> of Spreading Kindness
            </span>
            <Award className="w-3.5 h-3.5 text-ku-gold" />
          </div>
          <button
            type="button"
            onClick={() => setBannerVisible(false)}
            className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors z-10 p-1"
            aria-label="Dismiss banner"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ─── Navbar ─── */}
      <nav
        className={`ku-navbar ${scrolled ? "scrolled bg-white/95 backdrop-blur-xl" : "bg-white"}`}
        style={{ top: bannerHeight }}
      >
        <div className="ku-navbar-inner">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0 group">
            <img
              src={logo}
              alt="Kindness Unlimited"
              className={`object-contain transition-all duration-300 ${scrolled ? "h-10" : "h-14"}`}
            />
          </Link>

          {/* ─── Dashboard Navbar View (Student / Admin / Volunteer Dashboards) ─── */}
          {isDashboardRoute ? (
            <div className="flex items-center gap-3">
              <Link
                to={location.pathname}
                className="ku-nav-link active font-bold text-ku-purple flex items-center gap-1.5"
              >
                <LayoutDashboard className="w-4 h-4 text-ku-violet" />
                Dashboard
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#5D3294] text-white font-accent font-bold text-xs sm:text-sm hover:bg-[#462170] transition-all cursor-pointer shadow-sm"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>

              {/* 3 Lines (Hamburger) to trigger the rest of the navbar drawer */}
              <button
                type="button"
                className="flex items-center justify-center w-10 h-10 rounded-xl text-ku-purple hover:bg-ku-soft/50 transition-colors ml-1"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Toggle navigation drawer"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          ) : (
            /* ─── Standard Full Navbar (Any other page) ─── */
            <>
              {/* Desktop Nav */}
              <div className="hidden lg:flex items-center gap-1">
                {/* Home */}
                <Link to="/" className={`ku-nav-link flex items-center gap-1.5 ${isActive("/") ? "active" : ""}`}>
                  <Home className="w-4 h-4" />
                  Home
                </Link>

                {/* Impact Report */}
                <div ref={impactRef} className="relative">
                  <button
                    type="button"
                    onClick={() => { setImpactOpen(!impactOpen); setAboutOpen(false); setProgramsOpen(false); }}
                    className={`ku-nav-link flex items-center gap-1.5 ${isImpactActive ? "active" : ""}`}
                  >
                    <BarChart3 className="w-4 h-4" />
                    Impact Report
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${impactOpen ? "rotate-180" : ""}`} />
                  </button>
                  <div className={`ku-nav-dropdown ${impactOpen ? "open" : ""}`}>
                    {impactDropdown.map((item) => (
                      <Link key={item.to} to={item.to} className="ku-nav-dropdown-item flex items-center gap-3">
                        <item.icon className="w-4 h-4 text-ku-violet" />
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* About Us */}
                <div ref={aboutRef} className="relative">
                  <button
                    type="button"
                    onClick={() => { setAboutOpen(!aboutOpen); setProgramsOpen(false); setImpactOpen(false); }}
                    className={`ku-nav-link flex items-center gap-1.5 ${isAboutActive ? "active" : ""}`}
                  >
                    <Info className="w-4 h-4" />
                    About Us
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${aboutOpen ? "rotate-180" : ""}`} />
                  </button>
                  <div className={`ku-nav-dropdown ${aboutOpen ? "open" : ""}`}>
                    {aboutDropdown.map((item) => (
                      <Link key={item.to} to={item.to} className="ku-nav-dropdown-item flex items-center gap-3">
                        <item.icon className="w-4 h-4 text-ku-violet" />
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Volunteering */}
                <Link to="/volunteering" className={`ku-nav-link flex items-center gap-1.5 ${isActive("/volunteering") ? "active" : ""}`}>
                  <Users className="w-4 h-4" />
                  Volunteering
                </Link>

                {/* Programs */}
                <div ref={programsRef} className="relative">
                  <button
                    type="button"
                    onClick={() => { setProgramsOpen(!programsOpen); setAboutOpen(false); setImpactOpen(false); }}
                    className={`ku-nav-link flex items-center gap-1.5 ${isProgramsActive ? "active" : ""}`}
                  >
                    <Briefcase className="w-4 h-4" />
                    Programs
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${programsOpen ? "rotate-180" : ""}`} />
                  </button>
                  <div className={`ku-nav-dropdown ${programsOpen ? "open" : ""}`}>
                    {programsDropdown.map((item) => (
                      <Link key={item.to} to={item.to} className="ku-nav-dropdown-item flex items-center gap-3">
                        <item.icon className="w-4 h-4 text-ku-violet" />
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Merchandise */}
                <Link to="/shop" className={`ku-nav-link flex items-center gap-1.5 ${isActive("/shop") ? "active" : ""}`}>
                  <ShoppingBag className="w-4 h-4" />
                  Merchandise
                </Link>

                {/* Donate Button */}
                <Link to="/donate" className="ku-donate-btn ml-3">
                  <Heart className="w-4 h-4" fill="currentColor" />
                  Donate
                </Link>
              </div>

              {/* Mobile Bar: Donate + Hamburger */}
              <div className="flex items-center gap-2 lg:hidden">
                <Link
                  to="/donate"
                  className="ku-donate-btn !px-4 !py-2 !text-xs"
                >
                  <Heart className="w-3.5 h-3.5" fill="currentColor" />
                  Donate
                </Link>
                <button
                  type="button"
                  className="flex items-center justify-center w-10 h-10 rounded-xl text-ku-purple hover:bg-ku-soft/50 transition-colors"
                  onClick={() => setMobileOpen(!mobileOpen)}
                  aria-label="Toggle navigation"
                >
                  {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </div>
            </>
          )}
        </div>
      </nav>

      {/* ─── Drawer: Accessible on mobile OR when clicking 3 lines on Dashboard ─── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="ku-mobile-overlay"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              ref={mobileDrawerRef}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 250 }}
              className="ku-mobile-drawer"
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between p-5 border-b border-ku-soft/60">
                <Link to="/" className="flex items-center gap-2">
                  <img src={logo} alt="KU" className="h-10 w-auto" />
                </Link>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="w-9 h-9 rounded-full bg-ku-soft/50 flex items-center justify-center text-ku-purple"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Drawer Nav Items */}
              <nav className="mobile-nav-container flex-1 py-4 px-3 overflow-y-auto">
                <Link to="/" className={`mobile-nav-item mobile-nav-top-item ${isActive("/") ? "active" : ""}`}>
                  <span className="mobile-nav-icon-wrap">
                    <Home className="w-[18px] h-[18px]" />
                  </span>
                  Home
                </Link>

                <div className="mobile-nav-section">
                  <div className="mobile-nav-section-header">
                    <BarChart3 className="w-3.5 h-3.5" />
                    <span>Impact Report</span>
                  </div>
                  <div className="mobile-nav-section-items">
                    {impactDropdown.map((item) => (
                      <Link key={item.to} to={item.to} className={`mobile-nav-item mobile-nav-sub-item ${isExactActive(item.to) ? "active" : ""}`}>
                        <span className="mobile-nav-icon-wrap">
                          <item.icon className="w-4 h-4" />
                        </span>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="mobile-nav-section">
                  <div className="mobile-nav-section-header">
                    <Info className="w-3.5 h-3.5" />
                    <span>About Us</span>
                  </div>
                  <div className="mobile-nav-section-items">
                    {aboutDropdown.map((item) => (
                      <Link key={item.to} to={item.to} className={`mobile-nav-item mobile-nav-sub-item ${isActive(item.to) ? "active" : ""}`}>
                        <span className="mobile-nav-icon-wrap">
                          <item.icon className="w-4 h-4" />
                        </span>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>

                <Link to="/volunteering" className={`mobile-nav-item mobile-nav-top-item ${isActive("/volunteering") ? "active" : ""}`}>
                  <span className="mobile-nav-icon-wrap">
                    <HeartHandshake className="w-[18px] h-[18px]" />
                  </span>
                  Volunteering
                </Link>

                <div className="mobile-nav-section">
                  <div className="mobile-nav-section-header">
                    <Briefcase className="w-3.5 h-3.5" />
                    <span>Programs</span>
                  </div>
                  <div className="mobile-nav-section-items">
                    {programsDropdown.map((item) => (
                      <Link key={item.to} to={item.to} className={`mobile-nav-item mobile-nav-sub-item ${isActive(item.to) ? "active" : ""}`}>
                        <span className="mobile-nav-icon-wrap">
                          <item.icon className="w-4 h-4" />
                        </span>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>

                <Link to="/shop" className={`mobile-nav-item mobile-nav-top-item ${isActive("/shop") ? "active" : ""}`}>
                  <span className="mobile-nav-icon-wrap">
                    <ShoppingBag className="w-[18px] h-[18px]" />
                  </span>
                  Merchandise
                </Link>
              </nav>

              <div className="p-4 border-t border-ku-soft/60">
                <Link
                  to="/donate"
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-ku-gradient text-white font-accent font-bold text-sm shadow-elevated hover:scale-[1.02] transition-all"
                >
                  <Heart className="w-4 h-4" fill="currentColor" />
                  Donate Now
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ─── Page Content ─── */}
      <main style={{ paddingTop: bannerVisible ? '7.5rem' : '5rem' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname + location.search}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

export default NavbarLayout;